import PrimaryButton from '@/components/Buttons/PrimaryButton';
import SecondaryButton from '@/components/Buttons/SecondaryButton';
import GlassCard from '@/components/GlassCard';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import AppLayout from '@/layouts/AppLayout';
import { Evaluation, Question, UserAnswer } from '@/types';
import { cleanWordPressHtml } from '@/utils/cleanHtml';
import { router } from '@inertiajs/react';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface QuestionData {
    question: Question;
    user_answer: UserAnswer | null;
}

interface ReviewProps {
    evaluation: Evaluation;
    questions_data: QuestionData[];
    course: {
        slug: string;
    } | null;
    isPlacement?: boolean;
}

const ReviewContent: React.FC<ReviewProps> = ({
    evaluation,
    questions_data,
    course,
    isPlacement = false,
}) => {
    const { t } = useTranslation();
    const pageTitle = `Revisar - ${evaluation.title}`;
    const basePath = isPlacement ? '/placement-test' : '/evaluations';

    const getSelectedAnswerIds = (questionData: QuestionData) => {
        if (!questionData.user_answer) return [];
        const selectedIds = questionData.user_answer.selected_answer_ids || [];
        if (selectedIds.length > 0) return selectedIds;
        return questionData.user_answer.selected_answer_id
            ? [questionData.user_answer.selected_answer_id]
            : [];
    };

    const getClozeResponses = (questionData: QuestionData) =>
        questionData.question.question_type === 'cloze'
            ? (questionData.user_answer?.cloze_responses || []).filter((r) =>
                  r.trim()
              )
            : [];

    const getResponseData = (questionData: QuestionData) => {
        const type = questionData.question.question_type;
        if (type !== 'sort' && type !== 'matrix') return [];
        return (questionData.user_answer?.response_data || []).filter((r) =>
            r.trim()
        );
    };

    const questionIsAnswered = (questionData: QuestionData) =>
        getSelectedAnswerIds(questionData).length > 0 ||
        getClozeResponses(questionData).length > 0 ||
        getResponseData(questionData).length > 0;

    const answeredCount = questions_data.filter(questionIsAnswered).length;
    const unansweredCount = questions_data.length - answeredCount;

    const getAnswerText = (questionData: QuestionData) => {
        const type = questionData.question.question_type;
        if (type === 'cloze') {
            const responses = getClozeResponses(questionData);
            return responses.length > 0
                ? responses.join(', ')
                : t('evaluation.review.notAnswered');
        }

        if (type === 'sort') {
            const order = getResponseData(questionData);
            return order.length > 0
                ? order.join(' → ')
                : t('evaluation.review.notAnswered');
        }

        if (type === 'matrix') {
            const lefts = questionData.question.matrix?.lefts || [];
            const responses = questionData.user_answer?.response_data || [];
            const pairs = lefts
                .map((left, i) => `${left} → ${responses[i] || '—'}`)
                .join('; ');
            return pairs.length > 0
                ? pairs
                : t('evaluation.review.notAnswered');
        }

        const selectedAnswerIds = getSelectedAnswerIds(questionData);
        if (selectedAnswerIds.length === 0) {
            return t('evaluation.review.notAnswered');
        }

        const answers =
            questionData.question.answers?.filter((answer) =>
                selectedAnswerIds.includes(answer.id)
            ) || [];

        return answers.length > 0
            ? answers.map((answer) => answer.answer).join(', ')
            : t('evaluation.review.notAnswered');
    };

    const getAnswerLetter = (questionData: QuestionData) => {
        const selectedAnswerIds = getSelectedAnswerIds(questionData);
        if (selectedAnswerIds.length === 0) return '-';

        const letters = selectedAnswerIds
            .map((selectedId) =>
                questionData.question.answers?.findIndex(
                    (answer) => answer.id === selectedId
                )
            )
            .filter((index): index is number => index !== undefined && index >= 0)
            .map((index) => String.fromCharCode(65 + index));

        return letters.length > 0 ? letters.join(', ') : '-';
    };

    const handleSubmit = () => {
        router.post(`${basePath}/${evaluation.id}/review/`);
    };

    const handleBack = () => {
        // Go back to last question
        const lastQuestionIndex = questions_data.length - 1;
        router.get(
            `${basePath}/${evaluation.id}/questions/${lastQuestionIndex}/`
        );
    };

    return (
        <AppLayout>
            <PageTitle title={pageTitle} />
            <div className="min-h-screen">
                {/* Header */}
                <div className="border-b border-white/50 bg-white/30 backdrop-blur-sm dark:border-white/10 dark:bg-white/10">
                    <div className="mx-auto max-w-4xl px-4 py-6">
                        <h1 className="mb-2 text-2xl font-bold text-gray-800 dark:text-white">
                            {t('evaluation.review.title')}
                        </h1>
                        <p className="text-gray-600 dark:text-neutral-300">{evaluation.title}</p>
                    </div>
                </div>

                <div className="mx-auto max-w-4xl px-4 py-8">
                    {/* Summary Card */}
                    <GlassCard className="mb-8 p-6">
                        <h2 className="mb-4 text-xl font-semibold text-gray-800 dark:text-white">
                            {t('evaluation.review.summary')}
                        </h2>

                        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                            <div className="text-center">
                                <div className="text-theme-primary-text mb-1 text-3xl font-bold">
                                    {answeredCount}
                                </div>
                                <p className="text-sm text-gray-600 dark:text-neutral-400">
                                    {t('evaluation.review.answered')}
                                </p>
                            </div>

                            <div className="text-center">
                                <div
                                    className={`mb-1 text-3xl font-bold ${
                                        unansweredCount > 0
                                            ? 'text-red-600'
                                            : 'text-gray-400'
                                    }`}
                                >
                                    {unansweredCount}
                                </div>
                                <p className="text-sm text-gray-600 dark:text-neutral-400">
                                    {t('evaluation.review.unanswered')}
                                </p>
                            </div>

                            <div className="text-center">
                                <div className="mb-1 text-3xl font-bold text-gray-800 dark:text-white">
                                    {questions_data.length}
                                </div>
                                <p className="text-sm text-gray-600 dark:text-neutral-400">
                                    {t('evaluation.review.total')}
                                </p>
                            </div>
                        </div>
                    </GlassCard>

                    {/* Questions Review */}
                    <GlassCard className="mb-8 overflow-hidden">
                        <div className="border-b border-white/30 dark:border-white/10 px-6 py-4">
                            <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
                                {t('evaluation.review.yourAnswers')}
                            </h2>
                        </div>

                        <div className="divide-y divide-white/30 dark:divide-white/10">
                            {questions_data.map((questionData, index) => {
                                const isAnswered = questionData.user_answer;

                                return (
                                    <div
                                        key={questionData.question.id}
                                        className="px-6 py-4"
                                    >
                                        <div className="flex items-start gap-4">
                                            <div
                                                className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm font-medium ${
                                                    isAnswered
                                                        ? 'bg-theme-light text-theme-text'
                                                        : 'bg-gray-100 text-gray-500'
                                                }`}
                                            >
                                                {index + 1}
                                            </div>

                                            <div className="min-w-0 flex-1">
                                                <h3
                                                    className="mb-2 text-sm leading-relaxed font-medium text-gray-800 dark:text-neutral-100"
                                                    dangerouslySetInnerHTML={{
                                                        __html: cleanWordPressHtml(
                                                            questionData
                                                                .question
                                                                .question
                                                        ),
                                                    }}
                                                />

                                                <div className="flex items-start gap-3">
                                                    <div
                                                        className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-xs font-medium ${
                                                            isAnswered
                                                                ? 'bg-theme-primary text-theme-on-primary'
                                                                : 'bg-gray-300 text-gray-600'
                                                        }`}
                                                    >
                                                        {getAnswerLetter(
                                                            questionData
                                                        )}
                                                    </div>

                                                    <div
                                                        className={`flex-1 text-sm ${
                                                            isAnswered
                                                                ? 'text-gray-700 dark:text-neutral-300'
                                                                : 'text-gray-500 dark:text-neutral-500 italic'
                                                        }`}
                                                    >
                                                        {cleanWordPressHtml(
                                                            getAnswerText(questionData)
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </GlassCard>

                    {/* Action Buttons */}
                    <div className="flex flex-col justify-center gap-4 sm:flex-row">
                        <SecondaryButton onClick={handleBack} size="md">
                            {t('evaluation.review.backToQuestions')}
                        </SecondaryButton>

                        <PrimaryButton onClick={handleSubmit} size="md">
                            {t('evaluation.review.submitEvaluation')}
                        </PrimaryButton>
                    </div>
                </div>
            </div>
        </AppLayout>
    );
};

const Review: React.FC<ReviewProps> = (props) => {
    return (
        <ThemeWrapper>
            <ReviewContent {...props} />
        </ThemeWrapper>
    );
};

export default Review;
